/**
 * Input Atom
 *
 * A fundamental form input element for text entry.
 * Supports labels, descriptions, loading states, and icons.
 */

'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';
import { twMerge } from 'tailwind-merge';
import { Skeleton } from '../feedback/Skeleton';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  labelAction?: React.ReactNode;
  description?: string;
  isLoading?: boolean;
  wrapperClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, labelAction, description, isLoading, wrapperClassName, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = props.id || generatedId;
    return (
      <div className={twMerge('flex w-full flex-col', wrapperClassName)}>
        {Boolean(label) && (
          <div className={cn('mb-2 flex items-baseline', labelAction ? 'justify-between' : '')}>
            <label htmlFor={inputId} className='block text-base'>
              {label}
            </label>
            {labelAction}
          </div>
        )}
        <input
          id={inputId}
          type={isLoading ? 'hidden' : type}
          className={cn(
            'peer flex h-11 w-full justify-start border border-gray2 bg-transparent p-3 font-normal ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-zinc-500 invalid:border-2 invalid:border-primary-600 focus:border-b-2 focus-visible:border-zinc-700 focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:opacity-25 dark:border-zinc-800 dark:bg-zinc-950',
            className
          )}
          ref={ref}
          {...props}
        />
        {isLoading && <Skeleton className={twMerge('h-11 w-full', className)} />}
        {description && <p className='text-xs text-zinc-500 peer-invalid:text-primary-600'>{description}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };

interface IconInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  iconLeft?: React.ElementType;
  iconRight?: React.ElementType;
  rightIconClick?: () => void;
  leftIconClick?: () => void;
  placeholder?: string;
  label?: string;
  labelAction?: React.ReactNode;
  description?: string;
  disabled?: boolean;
}

export const IconInput = React.forwardRef<HTMLInputElement, IconInputProps>(
  (
    {
      iconLeft: IconLeft,
      iconRight: IconRight,
      rightIconClick,
      leftIconClick,
      placeholder,
      label,
      labelAction,
      description,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <div className='relative flex max-w-2xl items-center'>
        <Input
          ref={ref}
          disabled={disabled}
          description={description}
          label={label}
          labelAction={labelAction}
          placeholder={placeholder}
          className={`peer flex items-center ${IconLeft ? 'pl-10' : ''} ${IconRight ? 'pr-10' : ''}`}
          {...props}
        />
        {IconLeft && (
          <IconLeft
            strokeWidth={2.5}
            onClick={() => {
              if (!disabled && leftIconClick) {
                leftIconClick();
              }
            }}
            className={twMerge(
              'absolute left-3 h-4 w-4 transform text-muted-foreground peer-invalid:text-primary-500 peer-focus:text-foreground',
              disabled && 'text-slate-300',
              description ? 'top-[50px] -translate-y-1/2' : 'top-[38px] translate-y-1',
              leftIconClick && !disabled ? 'cursor-pointer' : 'cursor-not-allowed'
            )}
          />
        )}
        {IconRight && (
          <IconRight
            strokeWidth={2.5}
            onClick={() => {
              if (!disabled && rightIconClick) {
                rightIconClick();
              }
            }}
            className={twMerge(
              'absolute right-3 h-4 w-4 transform text-muted-foreground peer-invalid:text-primary-500 peer-focus:text-foreground',
              disabled && 'text-slate-300',
              description ? 'top-[50px] -translate-y-1/2' : 'top-[38px] translate-y-1',
              rightIconClick && !disabled ? 'cursor-pointer' : 'cursor-not-allowed'
            )}
          />
        )}
      </div>
    );
  }
);

IconInput.displayName = 'IconInput';
